import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { DiagnosticAnswer } from '@/data/diagnosticQuestions';
import { logAuditAction } from '@/hooks/useAuditLog';

/**
 * Último diagnóstico COMPLETADO del user. Filtra drafts.
 */
export function useDiagnosticResult() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['diagnostic', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('participant_diagnostics')
        .select('*')
        .eq('user_id', user.id)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

/**
 * Historial de diagnósticos COMPLETADOS del user. Filtra drafts.
 */
export function useDiagnosticHistory() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['diagnostic-history', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('participant_diagnostics')
        .select('id, maturity_level, dimension_scores, completed_at, answers')
        .eq('user_id', user.id)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });
}

interface SaveDiagnosticParams {
  answers: DiagnosticAnswer[];
  maturity_level: number;
  dimension_scores?: Record<string, number>;
}

/**
 * Al COMPLETAR el diagnóstico: si hay draft del user, lo UPDATE (upgrade
 * a completed_at = now). Si no hay draft, INSERT nuevo.
 */
export function useSaveDiagnostic() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SaveDiagnosticParams) => {
      if (!user) throw new Error('Not authenticated');

      const { data: existingDraft } = await supabase
        .from('participant_diagnostics')
        .select('id')
        .eq('user_id', user.id)
        .is('completed_at', null)
        .maybeSingle();

      const payload = {
        user_id: user.id,
        answers: params.answers as any,
        maturity_level: params.maturity_level,
        dimension_scores: params.dimension_scores as any,
        completed_at: new Date().toISOString(),
      };

      let data;
      if (existingDraft?.id) {
        const res = await supabase
          .from('participant_diagnostics')
          .update(payload)
          .eq('id', existingDraft.id)
          .select()
          .single();
        if (res.error) throw res.error;
        data = res.data;
      } else {
        const res = await supabase
          .from('participant_diagnostics')
          .insert(payload)
          .select()
          .single();
        if (res.error) throw res.error;
        data = res.data;
      }
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['diagnostic'] });
      queryClient.invalidateQueries({ queryKey: ['diagnostic-history'] });
      queryClient.invalidateQueries({ queryKey: ['participant-diagnostic-draft'] });
      if (user) {
        logAuditAction(user.id, 'diagnostic_complete', `Diagnóstico completado — Nivel ${data.maturity_level}`, { diagnostic_id: data.id, maturity_level: data.maturity_level } as any);
      }
    },
  });
}

export function useEvaluateDiagnostic() {
  return useMutation({
    mutationFn: async (params: {
      answers: DiagnosticAnswer[];
      personalInfo: { cargo: string; nombreEmpresa: string; sector: string; area: string };
    }) => {
      const { data, error } = await supabase.functions.invoke('evaluate-participant-diagnostic', {
        body: { answers: params.answers, personalInfo: params.personalInfo },
      });
      if (error) throw error;
      if (data?.ok === false) {
        throw new Error(data.error || 'Error al procesar el diagnóstico.');
      }
      return data as {
        level: string;
        levelNumber: number;
        dimensionScores: Record<string, number>;
        justification: string;
      };
    },
  });
}

/**
 * Draft server-side del diagnóstico individual. Se guarda en la misma tabla
 * `participant_diagnostics` con `completed_at IS NULL`. Solo 1 draft por user
 * (unique partial index).
 *
 * `dimension_scores` es donde persistimos info del step + personalInfo del draft
 * (misma jsonb column, con estructura {__draft: {step, personalInfo, currentQuestionIndex}}).
 * `answers` es directamente el array.
 */
export interface ParticipantDraftServer {
  id: string;
  user_id: string;
  answers: DiagnosticAnswer[] | null;
  dimension_scores: {
    __draft?: {
      step?: 'personal' | 'questions';
      personalInfo?: {
        birthDate?: string | null;
        cargo?: string | null;
        nombreEmpresa?: string | null;
        sector?: string | null;
        area?: string | null;
      };
      currentQuestionIndex?: number;
    };
  } | null;
  updated_at: string;
}

export function useLatestParticipantDraft() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['participant-diagnostic-draft', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('participant_diagnostics')
        .select('id, user_id, answers, dimension_scores, updated_at')
        .eq('user_id', user.id)
        .is('completed_at', null)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as ParticipantDraftServer | null;
    },
    enabled: !!user,
    staleTime: 30_000,
  });
}

interface SaveParticipantDraftParams {
  step: 'personal' | 'questions';
  personalInfo: {
    birthDate?: string | null;
    cargo?: string | null;
    nombreEmpresa?: string | null;
    sector?: string | null;
    area?: string | null;
  };
  answers: DiagnosticAnswer[];
  currentQuestionIndex: number;
}

export function useSaveParticipantDraft() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SaveParticipantDraftParams) => {
      if (!user) throw new Error('Not authenticated');

      const { data: existing } = await supabase
        .from('participant_diagnostics')
        .select('id')
        .eq('user_id', user.id)
        .is('completed_at', null)
        .maybeSingle();

      const draftMeta = {
        __draft: {
          step: params.step,
          personalInfo: params.personalInfo,
          currentQuestionIndex: params.currentQuestionIndex,
        },
      };

      const payload: any = {
        user_id: user.id,
        answers: params.answers as any,
        dimension_scores: draftMeta as any,
        completed_at: null,
      };

      if (existing?.id) {
        const { error } = await supabase
          .from('participant_diagnostics')
          .update(payload)
          .eq('id', existing.id);
        if (error) throw error;
        return existing.id;
      } else {
        const { data, error } = await supabase
          .from('participant_diagnostics')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        return data.id;
      }
    },
    onError: (error) => {
      console.error('[participant-draft] autosave failed:', error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participant-diagnostic-draft'] });
    },
  });
}

export function useDiscardParticipantDraft() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user) return;
      await supabase
        .from('participant_diagnostics')
        .delete()
        .eq('user_id', user.id)
        .is('completed_at', null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participant-diagnostic-draft'] });
    },
  });
}

/**
 * Autosave server-side debounced (1.5s) + flush en beforeunload / pagehide /
 * visibilitychange. Corre en paralelo al localStorage.
 */
export function useParticipantDraftAutosave(
  enabled: boolean,
  params: SaveParticipantDraftParams | null,
  debounceMs = 1500,
) {
  const saveDraft = useSaveParticipantDraft();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const paramsRef = useRef<SaveParticipantDraftParams | null>(null);
  const enabledRef = useRef<boolean>(false);
  paramsRef.current = params;
  enabledRef.current = enabled;

  useEffect(() => {
    if (!enabled || !params) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const current = paramsRef.current;
      if (!current) return;
      saveDraft.mutate(current);
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, JSON.stringify(params), debounceMs]);

  useEffect(() => {
    const flush = () => {
      if (!enabledRef.current) return;
      const current = paramsRef.current;
      if (!current) return;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      saveDraft.mutate(current);
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
